import { useState, useMemo } from 'react';
import axios from 'axios';
import { Plus, PencilSimple, Trash, CaretDown, CaretRight } from '@phosphor-icons/react';
import { toast } from 'sonner';
import EspaceFormDialog, { typeLabel } from './EspaceFormDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Construit une liste plate (DFS) des espaces racines + descendants avec
 * leur profondeur d'arborescence. Permet d'itérer sans récursion JSX
 * (évite les bugs babel-traverse "Maximum call stack size exceeded").
 */
function flattenEspaces(rootEspaces, allEspaces, expandedIds) {
  const result = [];
  const childrenByParent = new Map();
  for (const e of (allEspaces || [])) {
    if (e.parent_espace_id) {
      const arr = childrenByParent.get(e.parent_espace_id) || [];
      arr.push(e);
      childrenByParent.set(e.parent_espace_id, arr);
    }
  }
  const stack = (rootEspaces || []).map(e => ({ esp: e, depth: 0 }));
  // DFS itérative en pré-ordre
  while (stack.length) {
    const { esp, depth } = stack.shift();
    const children = childrenByParent.get(esp.id) || [];
    result.push({ esp, depth, hasChildren: children.length > 0, childrenCount: children.length });
    if (expandedIds.has(esp.id) && children.length) {
      // Pousser les enfants en tête (pour respecter l'ordre DFS)
      for (let i = children.length - 1; i >= 0; i--) {
        stack.unshift({ esp: children[i], depth: depth + 1 });
      }
    }
  }
  return result;
}

function EspaceRow({ row, batimentId, chantierId, lots, expandedIds, onToggleExpand, onChanged, onEdit, onAddChild }) {
  const { esp, depth, hasChildren, childrenCount } = row;
  const tInfo = typeLabel(esp.type);
  const expanded = expandedIds.has(esp.id);
  const lotsRattaches = esp.lots || [];
  const showNiveau = esp.niveau && esp.type !== 'etage';

  const remove = async () => {
    if (!window.confirm(`Supprimer l'espace « ${esp.nom} » ?${childrenCount ? ` (et ses ${childrenCount} sous-espaces)` : ''}`)) return;
    try {
      await axios.delete(`${API_URL}/api/chantiers/${chantierId}/espaces/${esp.id}?force=true`, { withCredentials: true });
      toast.success('Espace supprimé');
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur suppression');
    }
  };

  return (
    <div className="border-b border-[#F0F0ED] last:border-b-0" data-testid={`espace-node-${esp.id}`}>
      <div className="flex items-center gap-2 py-1.5 hover:bg-[#FAFAF8] group"
        style={{ paddingLeft: `${12 + depth * 24}px`, paddingRight: 12 }}>
        {hasChildren ? (
          <button onClick={() => onToggleExpand(esp.id)} className="text-[#6B6B6B] hover:text-[#2C2C2C]"
            data-testid={`espace-toggle-${esp.id}`}>
            {expanded ? <CaretDown size={12} /> : <CaretRight size={12} />}
          </button>
        ) : (<span style={{ width: 12 }} />)}
        <span className="text-sm" title={tInfo.l}>{tInfo.icon}</span>
        <span className="text-sm font-medium text-[#2C2C2C] flex-1 truncate" title={esp.nom}>{esp.nom}</span>
        {showNiveau && (
          <span className="text-[10px] text-[#6B6B6B] uppercase tracking-wider hidden sm:inline">{esp.niveau}</span>
        )}
        {esp.surface_totale && (
          <span className="text-[10px] text-[#6B6B6B]">{esp.surface_totale} m²</span>
        )}
        {lotsRattaches.length > 0 && (
          <div className="hidden md:flex items-center gap-1">
            {lotsRattaches.slice(0, 3).map(lot => (
              <span key={lot.id}
                className="text-[10px] px-1.5 py-0.5 bg-[#E3F2FD] text-[#1E3A5F] font-medium"
                title={`${lot.lot_code} — ${lot.nom}`}>
                {lot.lot_code || lot.nom?.slice(0, 12)}
              </span>
            ))}
            {lotsRattaches.length > 3 && (
              <span className="text-[10px] text-[#6B6B6B]">+{lotsRattaches.length - 3}</span>
            )}
          </div>
        )}
        <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 ml-auto">
          <button onClick={() => onAddChild(esp)} className="p-1 text-[#6B6B6B] hover:text-[#C75B39]"
            title="Ajouter un sous-espace" data-testid={`espace-add-child-${esp.id}`}>
            <Plus size={12} />
          </button>
          <button onClick={() => onEdit(esp)} className="p-1 text-[#6B6B6B] hover:text-[#1E3A5F]"
            title="Modifier" data-testid={`espace-edit-${esp.id}`}>
            <PencilSimple size={12} />
          </button>
          <button onClick={remove} className="p-1 text-[#6B6B6B] hover:text-[#8B3A3A]"
            title="Supprimer" data-testid={`espace-delete-${esp.id}`}>
            <Trash size={12} />
          </button>
        </div>
      </div>
    </div>
  );
}

function EspaceTree({ rootEspaces, allEspaces, batimentId, chantierId, lots, onChanged }) {
  const [expandedIds, setExpandedIds] = useState(() => {
    // Auto-expand initial : tous les noeuds expandés par défaut
    const ids = new Set();
    for (const e of (allEspaces || [])) ids.add(e.id);
    return ids;
  });
  const [editEspace, setEditEspace] = useState(null);
  const [addChildOf, setAddChildOf] = useState(null);

  const toggleExpand = (id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const flatRows = useMemo(
    () => flattenEspaces(rootEspaces, allEspaces, expandedIds),
    [rootEspaces, allEspaces, expandedIds]
  );

  return (
    <div>
      {flatRows.map(row => (
        <EspaceRow
          key={row.esp.id}
          row={row}
          batimentId={batimentId}
          chantierId={chantierId}
          lots={lots}
          expandedIds={expandedIds}
          onToggleExpand={toggleExpand}
          onChanged={onChanged}
          onEdit={setEditEspace}
          onAddChild={setAddChildOf}
        />
      ))}

      {editEspace && (
        <EspaceFormDialog
          chantierId={chantierId}
          batimentId={batimentId}
          espace={editEspace}
          lots={lots}
          onSaved={onChanged}
          onClose={() => setEditEspace(null)}
        />
      )}
      {addChildOf && (
        <EspaceFormDialog
          chantierId={chantierId}
          batimentId={batimentId}
          parentEspace={addChildOf}
          lots={lots}
          onSaved={() => { onChanged(); setExpandedIds(prev => new Set(prev).add(addChildOf.id)); }}
          onClose={() => setAddChildOf(null)}
        />
      )}
    </div>
  );
}

export default EspaceTree;
